import { useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { OfferingSearchResponse } from '@lio/api-types';
import { Dialog } from '@components/dialog';
import { Select } from '@components/select/Select';
import { ToastType } from '@components/toast/ToastType.enum';
import Loading from '@components/Loading';
import { useMigrateQuoteOffering, useQuote } from '@services/quotes';
import { useSearchOfferings } from '@services/offering';
import { useProductOptions } from '@services/productOption';
import { ArrayItem } from '@utils/commonTypes';

interface Props {
  quoteId: string;
  handleClose: () => void;
}

type OfferingItem = ArrayItem<OfferingSearchResponse['items']>;

export const SwitchOfferingDialog = ({ quoteId, handleClose }: Props) => {
  const { quote, isLoading: isQuoteLoading } = useQuote(quoteId);
  const { enqueueSnackbar } = useSnackbar();
  const { isPending, migrateQuoteOffering } = useMigrateQuoteOffering();

  const [selectedOffering, setSelectedOffering] = useState<OfferingItem | null>(null);
  const [selectedBusinessClassId, setSelectedBusinessClassId] = useState<string>('');

  // The offering search endpoint filters by writing company server-side, so this is a single
  // request scoped to the quote's writing company.
  const { offerings: offeringsResponse, isLoading: loadingOfferings } = useSearchOfferings(
    { writingCompanyId: quote?.writingCompany?.id, pageLimit: 1000 },
    Boolean(quote?.writingCompany?.id),
  );

  // Exclude the offering the quote is already on.
  const availableOfferings = (offeringsResponse?.items ?? []).filter((offering) => offering.id !== quote?.offering?.id);

  const { productOptions, isLoading: loadingBusinessClasses } = useProductOptions(
    selectedOffering?.insuranceProduct?.id,
  );

  const handleOfferingChange = (offeringId: string) => {
    const offering = availableOfferings.find((item) => item.id === offeringId) ?? null;
    setSelectedOffering(offering);
    // Clear the dependent business class whenever the offering changes to avoid a mismatch.
    setSelectedBusinessClassId('');
  };

  const isSubmitDisabled = !selectedOffering || !selectedBusinessClassId;

  const handleSubmit = async () => {
    if (!quote || !selectedOffering || !selectedBusinessClassId) return;

    try {
      await migrateQuoteOffering({
        quoteId: quote.id,
        newOfferingId: selectedOffering.id,
        newBusinessClassId: selectedBusinessClassId,
      });

      handleClose();
      // The BE migrates in place and keeps the same quote id, so reload to reflect the new
      // offering and business class.
      window.location.reload();
    } catch {
      enqueueSnackbar('Failed to switch offering', {
        variant: ToastType.Error,
        persist: true,
      });
    }
  };

  if (isQuoteLoading) {
    return (
      <Box data-testid='content-loading-container' sx={sxStyles.loadingContainer}>
        <Loading />
      </Box>
    );
  }

  // When the quote fails to load (network/API error), surface the error and let the user exit
  // rather than leaving the loading spinner up indefinitely.
  if (!quote) {
    return (
      <Dialog
        open
        title='Switch Offering'
        secondaryBtnAction={handleClose}
        secondaryBtnText='Exit'
        handleCloseDialog={handleClose}
      >
        <Box data-testid='switch-offering-error' sx={sxStyles.formGrid}>
          <Typography variant='bodySmall'>
            We couldn&apos;t load this quote. Please close this dialog and try again.
          </Typography>
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      title={`Switch Offering of #${quote.policyNumber?.policyNumber}`}
      primaryBtnAction={handleSubmit}
      primaryBtnText='Switch Offering'
      isPrimaryBtnLoading={isPending}
      isPrimaryBtnDisabled={isSubmitDisabled}
      secondaryBtnAction={handleClose}
      secondaryBtnText='Exit'
      handleCloseDialog={handleClose}
    >
      {loadingOfferings ? (
        <Box data-testid='content-loading-container' sx={sxStyles.loadingContainer}>
          <Loading />
        </Box>
      ) : (
        <Grid size={{ xs: 11 }} sx={sxStyles.formGrid}>
          <Select
            id='Offering'
            value={selectedOffering?.id ?? ''}
            inputLabel='Offering'
            variant='outlined'
            color='secondary'
            onChange={(e) => handleOfferingChange(e.target.value)}
            fullWidth
            items={availableOfferings
              .map((item) => ({
                key: item.id,
                value: item.id,
                label: item.label ?? item.insuranceProduct.name,
              }))
              .sort((a, b) => a.label.localeCompare(b.label))}
            sx={sxStyles.select}
          />

          <Select
            id='BusinessClass'
            value={selectedBusinessClassId}
            inputLabel='Business Class'
            variant='outlined'
            color='secondary'
            disabled={!selectedOffering || loadingBusinessClasses}
            onChange={(e) => setSelectedBusinessClassId(e.target.value)}
            fullWidth
            items={productOptions.items.map((item) => ({
              key: item.id,
              value: item.id,
              label: item.name,
            }))}
            sx={sxStyles.select}
          />
        </Grid>
      )}
    </Dialog>
  );
};

const sxStyles = {
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '345px',
  },
  formGrid: {
    maxWidth: 'none !important',
    width: '100%',
    padding: '26px',
    '.MuiFormControl-root': {
      width: '100%',
    },
  },
  select: { marginBottom: '24px' },
};
